import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Tooltip,
    Typography,
} from '@mui/material'
import { ReactElement, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { LocalizationObject } from '../../../../redux/slices/userSlice'
import { getStatusIconPath } from '../../../../utils/AlertUtils'
import { formatDateTimeWithUserPreferences } from '../../../../utils/DateAndTimeUtils'
import { Alert } from './interfaces'
import { UseAlertNotesResult } from './useAlertNotes'
import { buildAlertTimeline, getAlertKey } from './utils'

interface AlertTimelinePanelProps {
    alert: Alert
    canWrite: boolean
    userLocalization?: Partial<LocalizationObject>
    notes: UseAlertNotesResult
}

/**
 * The Alerts table's expandable row detail: the Acknowledgment Status
 * timeline (merged notes + status-change history) plus the add/edit-note UI.
 */
const AlertTimelinePanel = ({
    alert,
    canWrite,
    userLocalization,
    notes,
}: AlertTimelinePanelProps): ReactElement => {
    const { t } = useTranslation()
    const {
        editingNoteAlertId,
        setEditingNoteAlertId,
        noteValue,
        setNoteValue,
        editableRef,
        initialEditableContentRef,
        handleStopPropagation,
        handleEditNoteClick,
        handleCancelNoteEdit,
        handleUpdateNoteClick,
        handleSaveNoteClick,
        handleNewNoteFocus,
    } = notes

    const combined = useMemo(
        () => buildAlertTimeline(alert.notes, alert.statusHistory),
        [alert.notes, alert.statusHistory],
    )

    // `alert.id` is a rule/condition dedup key, not unique per row (see
    // matchesAlert/getAlertKey in utils.ts) - a repeat alert can share it
    // with another row. The shared editable-note ref/state must key off the
    // composite row key instead, or two rows fight over the same DOM node.
    const alertKey = getAlertKey(alert)

    return (
        <Box
            sx={{
                px: 0,
                backgroundColor: '#D2ECFF',
                padding: '0px 16px',
                borderRight: '6px solid #FFFFFF',
                borderLeft: '6px solid #FFFFFF',
            }}
        >
            <Accordion
                disableGutters
                elevation={0}
                square
                sx={{
                    background: 'transparent',
                    '&:before': { display: 'none' },
                }}
            >
                <AccordionSummary
                    sx={{
                        backgroundColor: '#D2ECFF',
                        minHeight: 32,
                        '& .MuiAccordionSummary-content': {
                            margin: 0,
                            alignItems: 'center',
                        },
                        '&.Mui-expanded .ack-arrow': {
                            transform: 'rotate(180deg)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#666666',
                            }}
                        >
                            {t('Acknowledgment Status')}
                        </Typography>
                        <Box
                            className="ack-arrow"
                            sx={{
                                fontSize: 12,
                                color: '#607d8b',
                                transition: 'transform 0.2s ease',
                            }}
                        >
                            ▼
                        </Box>
                    </Box>
                </AccordionSummary>

                <AccordionDetails
                    sx={{
                        background: '#E9F6FF',
                        px: 2,
                        py: 1,
                        borderBottom: '10px solid #D2ECFF',
                    }}
                >
                    {combined.length === 0 && (
                        <Typography fontSize={14}>
                            {t('No history available')}
                        </Typography>
                    )}

                    {combined.map((item, idx) => {
                        const icon = item.isStatus
                            ? getStatusIconPath(item.status!, alert.severity)
                            : undefined
                        return (
                            <Box
                                key={item.id || idx}
                                sx={{
                                    py: 1,
                                    borderBottom: '1px solid #D2ECFF',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 4,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            flex: 1,
                                        }}
                                    >
                                        {t('Acknowledged By')} :{' '}
                                        <span
                                            style={{
                                                color: '#666666',
                                            }}
                                        >
                                            {item.userEmail} (
                                            {formatDateTimeWithUserPreferences(
                                                item.createdAt,
                                                userLocalization,
                                            )}
                                            )
                                        </span>
                                    </Typography>
                                    <Box
                                        sx={{
                                            width: 36,
                                            display: 'flex',
                                            justifyContent: 'flex-end',
                                        }}
                                    >
                                        {item.isStatus && (
                                            <Box
                                                component="img"
                                                src={icon}
                                                sx={{
                                                    width: 24,
                                                    height: 24,
                                                }}
                                            />
                                        )}
                                    </Box>
                                </Box>
                                {!item.isStatus && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            gap: 1,
                                            mt: 0.5,
                                        }}
                                        onClick={handleStopPropagation}
                                    >
                                        <Typography
                                            sx={{
                                                fontSize: 14,
                                                fontWeight: 500,
                                                whiteSpace: 'nowrap',
                                                pt: '4px',
                                            }}
                                        >
                                            {t('Notes')}:
                                        </Typography>
                                        {canWrite &&
                                        editingNoteAlertId === item.id ? (
                                            <>
                                                <Box
                                                    ref={editableRef}
                                                    contentEditable
                                                    suppressContentEditableWarning
                                                    onInput={(e) => {
                                                        setNoteValue(
                                                            e.currentTarget.innerHTML.trim(),
                                                        )
                                                    }}
                                                    onKeyDown={(e) => {
                                                        const navKeys = [
                                                            'ArrowLeft',
                                                            'ArrowRight',
                                                            'ArrowUp',
                                                            'ArrowDown',
                                                            'Enter',
                                                        ]
                                                        if (
                                                            navKeys.includes(
                                                                e.key,
                                                            )
                                                        ) {
                                                            e.stopPropagation()
                                                        }
                                                        if (
                                                            (e.ctrlKey ||
                                                                e.metaKey) &&
                                                            [
                                                                'b',
                                                                'i',
                                                                'u',
                                                            ].includes(
                                                                e.key.toLowerCase(),
                                                            )
                                                        ) {
                                                            e.preventDefault()
                                                            e.stopPropagation()
                                                            const cmd =
                                                                e.key.toLowerCase() ===
                                                                'b'
                                                                    ? 'bold'
                                                                    : e.key.toLowerCase() ===
                                                                        'i'
                                                                      ? 'italic'
                                                                      : 'underline'
                                                            document.execCommand(
                                                                cmd,
                                                                false,
                                                            )
                                                            setNoteValue(
                                                                e.currentTarget.innerHTML.trim(),
                                                            )
                                                        }
                                                    }}
                                                    sx={{
                                                        flex: 1,
                                                        minHeight: '30px',
                                                        padding: '6px',
                                                        borderRadius: '4px',
                                                        border: '1px solid #1976d2',
                                                        outline: 'none',
                                                        backgroundColor:
                                                            '#ffffff',
                                                        fontSize: '14px',
                                                        fontFamily:
                                                            '-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif',
                                                        '&:empty::before': {
                                                            content: `"${t('addANote')}..."`,
                                                            color: '#999',
                                                        },
                                                    }}
                                                />
                                                {noteValue
                                                    .replace(/<[^>]*>/g, '')
                                                    .trim() !== '' && (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            gap: 1,
                                                            pt: '4px',
                                                        }}
                                                    >
                                                        <Tooltip
                                                            title={t('save')}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src="/assets/images/icons/tick.svg"
                                                                alt="Save"
                                                                sx={{
                                                                    width: 30,
                                                                    height: 30,
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        opacity: 0.8,
                                                                    },
                                                                    border: '1px solid #C4C4C4',
                                                                    borderRadius:
                                                                        '2px',
                                                                    padding:
                                                                        '5px',
                                                                }}
                                                                onClick={(e) =>
                                                                    handleUpdateNoteClick(
                                                                        e,
                                                                        alert,
                                                                        item.id,
                                                                    )
                                                                }
                                                            />
                                                        </Tooltip>
                                                        <Tooltip
                                                            title={t('cancel')}
                                                        >
                                                            <Box
                                                                component="img"
                                                                src="/assets/images/icons/cancel.svg"
                                                                alt="Cancel"
                                                                sx={{
                                                                    width: 30,
                                                                    height: 30,
                                                                    cursor: 'pointer',
                                                                    '&:hover': {
                                                                        opacity: 0.8,
                                                                    },
                                                                    border: '1px solid #C4C4C4',
                                                                    borderRadius:
                                                                        '2px',
                                                                    padding:
                                                                        '7px',
                                                                }}
                                                                onClick={
                                                                    handleCancelNoteEdit
                                                                }
                                                            />
                                                        </Tooltip>
                                                    </Box>
                                                )}
                                            </>
                                        ) : (
                                            <Box
                                                sx={{
                                                    fontSize: 14,
                                                    color: '#666666',
                                                    pt: '4px',
                                                    cursor: canWrite
                                                        ? 'pointer'
                                                        : 'default',
                                                    '&:hover': canWrite
                                                        ? {
                                                              textDecoration:
                                                                  'underline',
                                                          }
                                                        : {},
                                                }}
                                                dangerouslySetInnerHTML={{
                                                    __html:
                                                        item.noteText ||
                                                        '(empty)',
                                                }}
                                                onClick={() =>
                                                    handleEditNoteClick(
                                                        item.id,
                                                        item.noteText,
                                                    )
                                                }
                                            />
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )
                    })}

                    {canWrite && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 1,
                                mt: 1.5,
                                mb: 0.5,
                            }}
                            onClick={handleStopPropagation}
                        >
                            <Box
                                ref={
                                    editingNoteAlertId === alertKey
                                        ? editableRef
                                        : undefined
                                }
                                contentEditable
                                suppressContentEditableWarning
                                onInput={(e) => {
                                    const currentNoteValue =
                                        e.currentTarget.innerHTML.trim()
                                    if (editingNoteAlertId !== alertKey) {
                                        initialEditableContentRef.current =
                                            currentNoteValue
                                        setEditingNoteAlertId(alertKey)
                                    }
                                    setNoteValue(currentNoteValue)
                                }}
                                onFocus={() => handleNewNoteFocus(alertKey)}
                                onKeyDown={(e) => {
                                    const navKeys = [
                                        'ArrowLeft',
                                        'ArrowRight',
                                        'ArrowUp',
                                        'ArrowDown',
                                        'Enter',
                                    ]
                                    if (navKeys.includes(e.key)) {
                                        e.stopPropagation()
                                    }
                                    if (
                                        (e.ctrlKey || e.metaKey) &&
                                        ['b', 'i', 'u'].includes(
                                            e.key.toLowerCase(),
                                        )
                                    ) {
                                        e.preventDefault()
                                        e.stopPropagation()
                                        const cmd =
                                            e.key.toLowerCase() === 'b'
                                                ? 'bold'
                                                : e.key.toLowerCase() === 'i'
                                                  ? 'italic'
                                                  : 'underline'
                                        document.execCommand(cmd, false)
                                        setNoteValue(
                                            e.currentTarget.innerHTML.trim(),
                                        )
                                    }
                                }}
                                sx={{
                                    width: '100%',
                                    minHeight: '30px',
                                    padding: '6px',
                                    borderRadius: '4px',
                                    border: '1px solid #1976d2',
                                    outline: 'none',
                                    backgroundColor: '#ffffff',
                                    fontSize: '14px',
                                    fontFamily:
                                        '-apple-system,BlinkMacSystemFont,"Segoe UI","Helvetica Neue",Helvetica,Arial,sans-serif',
                                    transition: 'all 0.2s ease',
                                    '&:empty::before': {
                                        content: `"${t('addANote')}..."`,
                                        color: '#999',
                                    },
                                }}
                            />

                            {editingNoteAlertId === alertKey &&
                                noteValue.replace(/<[^>]*>/g, '').trim() !==
                                    '' && (
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            gap: 1,
                                            paddingTop: '4px',
                                        }}
                                    >
                                        <Tooltip title={t('save')}>
                                            <Box
                                                component="img"
                                                src="/assets/images/icons/tick.svg"
                                                alt="Save"
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        opacity: 0.8,
                                                    },
                                                    border: '1px solid #C4C4C4',
                                                    borderRadius: '2px',
                                                    padding: '5px',
                                                }}
                                                onClick={(e) =>
                                                    handleSaveNoteClick(
                                                        e,
                                                        alert,
                                                    )
                                                }
                                            />
                                        </Tooltip>

                                        <Tooltip title={t('cancel')}>
                                            <Box
                                                component="img"
                                                src="/assets/images/icons/cancel.svg"
                                                alt="Cancel"
                                                sx={{
                                                    width: 30,
                                                    height: 30,
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        opacity: 0.8,
                                                    },
                                                    border: '1px solid #C4C4C4',
                                                    borderRadius: '2px',
                                                    padding: '7px',
                                                }}
                                                onClick={handleCancelNoteEdit}
                                            />
                                        </Tooltip>
                                    </Box>
                                )}
                        </Box>
                    )}
                </AccordionDetails>
            </Accordion>
        </Box>
    )
}

export default AlertTimelinePanel
